import {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Button from '../../../../components/button';
import MainFooter from '../../../../components/mazraeKit/footers/footerMain';
import CustomHeader from '../../../../components/mazraeKit/headers/homeHeader';
import NoItemFoundPage from '../../../../components/noItemFoundPage';
import PageLoader from '../../../../components/pageLoader';
import SpinnerLoading from '../../../../components/SpinnerLoading';
import useGetListLoanRequest from '../../../../services/hookService/getListLoanRequest/useGetListLoanRequest';
import useGetListLoanType from '../../../../services/hookService/getListLoanType/useGetListLoanType';
import {receiveLoanSteps} from '../../receiveLoan/receiveLoanSteps';
import CancelLoanBottomSheet from '../cancelLoanRequest/CancelLoanBottomSheet';
import CancelLoanConfirmModal from '../cancelLoanRequest/CancelLoanConfirmModal';
import MultiStepComponent from '../MultiStepComponent';
import {receiveCreditSteps} from '../receiveCreditSteps';
import {useMultiStepForm} from '../useMultiStepForm';
import {useParamsFilter} from './helpers/useLoanFilters';
import useLoanTypeFilters, {
  repaymentTimeReverseMap,
} from './hooks/useLoanTypeFilters';
import LoanCard from './LoanCard';
import LoanCarouselFilter from './LoanCarouselFilter';
import LoansInfoBtmSheet from './LoansInfoBtmSheet';
import LoanSliderFilter from './LoanSliderFilter';
import SubmitLoanBtmSheet from './SubmitLoanBtmSheet';

const SelectLoanPage = () => {
  const navigate = useNavigate();

  const {data: listLoanRequest, isLoading: isLoadingListLoanRequest} =
    useGetListLoanRequest({is_finished: false});
  const loanItem = listLoanRequest as ILoanRequestItem | undefined;
  const {stepNumber} = useMultiStepForm();

  const {data, isLoading: isListLoanTypeLoading} = useGetListLoanType(
    Boolean(window?.location?.href?.includes('receive-loan')),
  );

  const [selectedLoan, setSelectedLoan] = useState<ILoanType | null>(null);
  const [isOpenInfoBtm, setIsOpenInfoBtm] = useState(false);
  const [isOpenSubmitBtm, setIsOpenSubmitBtm] = useState(false);
  const [isOpenActionBtm, setIsOpenActionBtm] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const {filteredLoans} = useLoanTypeFilters({
    searchParams,
    data: data,
  });
  const {getFilter, setFilter} = useParamsFilter();

  useEffect(() => {
    if (!data || data.length === 0) return;

    const firstLoan = data[0];
    const currentAmount = getFilter('amount');
    const currentRepayment = getFilter('repaymentTime');

    // If both are missing, set both at once
    if (!currentAmount && !currentRepayment) {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('amount', (firstLoan.amount / 1000000).toString());
          newParams.set(
            'repaymentTime',
            repaymentTimeReverseMap[firstLoan.total_repayment_time] ||
              'twelve-months',
          );
          return newParams;
        },
        {replace: true},
      );
      return;
    }

    // Set only amount if missing
    if (!currentAmount) {
      setFilter('amount', (firstLoan.amount / 1000000).toString());
    }

    // Set only repayment time if missing
    if (!currentRepayment) {
      const defaultRepayment =
        repaymentTimeReverseMap[firstLoan.total_repayment_time] ||
        'twelve-months';
      setFilter('repaymentTime', defaultRepayment);
    }
  }, [data]);

  /** ---------------- HANDLERS ---------------- */
  const handleSubmitLoan = () => {
    setIsOpenSubmitBtm(true);
  };

  const isLoading = isListLoanTypeLoading || isLoadingListLoanRequest;

  if (isLoading) return <PageLoader />;

  return (
    <div className="flex flex-col w-full bg-white h-dvh">
      {/* ---------------- HEADER ---------------- */}
      <CustomHeader
        preComponent={() => (
          <div
            className="relative flex items-center gap-2"
            onClick={() => navigate('/my-credits')}
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
            <span className="font-medium">انتخاب اعتبار</span>
          </div>
        )}
        postComponent={() => (
          <i
            className="icon-nameInfo-menu-1 text-[25px] text-gray-700"
            onClick={() => setIsOpenActionBtm(true)}
          />
        )}
      />

      {/* ---------------- CONTENT ---------------- */}
      <div className="flex flex-col w-full h-full gap-3 p-3 overflow-y-auto">
        {isLoading && (
          <div className="grid h-full place-content-center">
            <SpinnerLoading />
          </div>
        )}

        <MultiStepComponent
          steps={loanItem?.is_direct ? receiveLoanSteps : receiveCreditSteps}
          currentStep={stepNumber}
        />

        {!isLoading && data && (
          <>
            {/* ---------------- LOANS ---------------- */}
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm font-medium text-gray-800">
                  وام و اعتبار
                </span>
                <i
                  className="text-blue-600 icon-namereport1"
                  onClick={() => setIsOpenInfoBtm(true)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 ">
              <LoanSliderFilter />
              <LoanCarouselFilter />
            </div>

            {filteredLoans.length === 0 ? (
              <NoItemFoundPage
                className="h-[50dvh] px-6 text-center"
                title="با توجه به رتبه اعتباری و گزینه‌های انتخابی وامی وجود ندارد.  "
              />
            ) : (
              <div className="space-y-4">
                {filteredLoans.map(item => (
                  <LoanCard
                    key={item.guid}
                    loan={item}
                    selectedLoan={selectedLoan}
                    setSelectedLoan={setSelectedLoan}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ---------------- FOOTER ---------------- */}
      <div className="flex flex-col w-full gap-2 p-3 bg-white">
        <Button disabled={!selectedLoan} onClick={handleSubmitLoan}>
          ثبت درخواست وام
        </Button>
      </div>

      <MainFooter isScrolling={false} />

      <LoansInfoBtmSheet isOpen={isOpenInfoBtm} setIsOpen={setIsOpenInfoBtm} />

      <CancelLoanBottomSheet
        isOpen={isOpenActionBtm}
        setIsOpen={setIsOpenActionBtm}
        onConfirm={() => setIsCancelModalOpen(true)}
      />

      <CancelLoanConfirmModal
        isOpen={isCancelModalOpen}
        setIsOpen={setIsCancelModalOpen}
        loanGuid={loanItem?.guid as string}
        title="لغو درخواست"
        confirmationText="از لغو درخواست وام اطمینان دارید؟"
        removeText="لغو درخواست"
      />

      <SubmitLoanBtmSheet
        isOpenSubmitBtm={isOpenSubmitBtm}
        setIsOpenSubmitBtm={setIsOpenSubmitBtm}
        selectedLoan={selectedLoan}
        loanRequestGuid={loanItem?.guid as string}
        isDirectLoan={loanItem?.is_direct as boolean}
      />
    </div>
  );
};

export default SelectLoanPage;
